/* eslint-disable */
import {
  AudioPopupDefinition,
  AUDIO_POPUP,
  IMAGE_MAP2,
  VIDEO_POPUP,
  VideoPopupDefinition,
  VIDEO,
  VideoDefinition,
  ImageMap2Definition
} from '../items';
import { Translator } from '../Translator';
import { translate } from './translate';
import { t } from 'ttag';
import { isIOS, isSafari, isMobile } from 'react-device-detect';

import {
  INTERACTIVE4_BTN_SIZE as BTN_SIZE,
  clampHotspotPixelsToImage,
  resolveInteractive4Overlaps,
  usableHotspotImageSize
} from './interactive4Overlap';
import { resolveHotspotMarker } from './resolveHotspotMarker';

/**
 * Same percentage basis as Interactive4 `getCoordinatesInPercentage`: pixel coords on the
 * source image → % of width/height (used for overlap math and layout).
 */
const getCoordinatesInPercentage = (
  leftPx: number,
  topPx: number,
  maxWidth: number,
  maxHeight: number
) => ({
  left: (leftPx * 100) / maxWidth,
  top: (topPx * 100) / maxHeight
});

const clampMobile80 = (left: number, top: number) => {
  let l = left;
  let t = top;
  if (isMobile) {
    if (l > 80) l = 80;
    if (t > 80) t = 80;
  }
  return { left: l, top: t };
};

export const Interactive3: Translator = sequence => {
  let imageDimensions: { width: number; height: number; color?: string };

  return translate(sequence, item => {
    if (item.__typename === 'ImageMap') {
      const newItemPicture = sequence.items?.find(
        i => i.__typename === 'Background'
      );

      const pictureWidth =
        (item as any).pictures?.[0]?.width ??
        (newItemPicture as any)?.pictures?.[0]?.width;
      const pictureHeight =
        (item as any).pictures?.[0]?.height ??
        (newItemPicture as any)?.pictures?.[0]?.height;
      /** 1×1 solid backgrounds etc. are treated as "no size" → 1024 fallback. */
      const intrinsicSize = usableHotspotImageSize(pictureWidth, pictureHeight);

      imageDimensions = {
        width: intrinsicSize?.width ?? 1024,
        height: intrinsicSize?.height ?? 1024,
        color: (newItemPicture as any)?.pictures?.[0]?.color || ''
      };

      /** Intrinsic size for % coords + overlap safe-distance (matches ImageMap2 editor nudge). */
      const originMaxWidth = Math.max(1, imageDimensions.width);
      const originMaxHeight = Math.max(1, imageDimensions.height);
      const clampImageW = intrinsicSize?.width ?? null;
      const clampImageH = intrinsicSize?.height ?? null;

      return {
        type: IMAGE_MAP2,
        id: item.id,
        values: {
          imageUrl:
            (item as any).pictures?.[0]?.mediaUrl ||
            (newItemPicture as any)?.pictures?.[0]?.mediaUrl,
          body: t`Click the icons to continue.`,
          isFullScreen: true,
          color: imageDimensions.color || ''
        },
        items: (() => {
          const rawItems = item.items || [];

          const positions = rawItems.map(i => {
            const initialParts = (i.coordinates || '').split(',');
            const xPx = initialParts[0] ? Number(initialParts[0]) : 0;
            const yPx = initialParts[1] ? Number(initialParts[1]) : 0;
            const clamped = clampHotspotPixelsToImage(
              Number.isFinite(xPx) ? xPx : 0,
              Number.isFinite(yPx) ? yPx : 0,
              clampImageW,
              clampImageH
            );
            const basePct = getCoordinatesInPercentage(
              clamped.x,
              clamped.y,
              originMaxWidth,
              originMaxHeight
            );
            const parts: string[] = [
              basePct.left.toString(),
              basePct.top.toString()
            ];

            // Handle dynamically added popup items (our new audio points)
            if (
              ((i as any).type === 'POPUP' ||
                (i as any).type === 'AudioPopup') &&
              ((i.coordinates && i.coordinates !== 'null') ||
                ((i as any).values?.left !== undefined &&
                  (i as any).values?.left !== null) ||
                ((i as any).values?.top !== undefined &&
                  (i as any).values?.top !== null))
            ) {
              let leftPercent = 0;
              let topPercent = 0;
              const coordinates =
                i?.coordinates || (i as any)?.settings?.coordinates || '';

              if ((i as any).type === 'AudioPopup' && (i as any).values) {
                leftPercent = parseFloat((i as any).values.left) || 0;
                topPercent = parseFloat((i as any).values.top) || 0;
              } else {
                const coordParts = coordinates.split(',');
                const xCoord = coordParts[0] ? Number(coordParts[0]) : 0;
                const yCoord = coordParts[1] ? Number(coordParts[1]) : 0;
                const clampedCoords = clampHotspotPixelsToImage(
                  Number.isFinite(xCoord) ? xCoord : 0,
                  Number.isFinite(yCoord) ? yCoord : 0,
                  clampImageW,
                  clampImageH
                );
                const c = getCoordinatesInPercentage(
                  clampedCoords.x,
                  clampedCoords.y,
                  originMaxWidth,
                  originMaxHeight
                );
                leftPercent = c.left;
                topPercent = c.top;
              }
              const finiteL = Number.isFinite(leftPercent) ? leftPercent : 0;
              const finiteT = Number.isFinite(topPercent) ? topPercent : 0;
              return clampMobile80(finiteL, finiteT);
            }

            const leftFromParts = parts[0] ? parseFloat(parts[0]) : 0;
            const topFromParts = parts[1] ? parseFloat(parts[1]) : 0;
            const finiteL = Number.isFinite(leftFromParts) ? leftFromParts : 0;
            const finiteT = Number.isFinite(topFromParts) ? topFromParts : 0;
            return clampMobile80(finiteL, finiteT);
          });

          if (positions.length > 1) {
            resolveInteractive4Overlaps(
              positions,
              isMobile,
              originMaxWidth,
              originMaxHeight
            );
          }

          return rawItems.map((i, index) => {
            const initialParts = (i.coordinates || '').split(',');
            const xPx2 = initialParts[0] ? Number(initialParts[0]) : 0;
            const yPx2 = initialParts[1] ? Number(initialParts[1]) : 0;
            const clamped2 = clampHotspotPixelsToImage(
              Number.isFinite(xPx2) ? xPx2 : 0,
              Number.isFinite(yPx2) ? yPx2 : 0,
              clampImageW,
              clampImageH
            );
            const basePct2 = getCoordinatesInPercentage(
              clamped2.x,
              clamped2.y,
              originMaxWidth,
              originMaxHeight
            );
            const parts: string[] = [
              basePct2.left.toString(),
              basePct2.top.toString()
            ];

            const resolvedLeft = positions[index].left;
            const resolvedTop = positions[index].top;

            // Handle dynamically added popup items (our new audio points)
            // Process any POPUP item that has coordinates (regardless of audio)
            if (
              ((i as any).type === 'POPUP' ||
                (i as any).type === 'AudioPopup') &&
              ((i.coordinates && i.coordinates !== 'null') ||
                ((i as any).values?.left !== undefined &&
                  (i as any).values?.left !== null) ||
                ((i as any).values?.top !== undefined &&
                  (i as any).values?.top !== null))
            ) {
              const coordinates =
                i?.coordinates || (i as any)?.settings?.coordinates || '';

              const legacyMarkerTitle =
                item.variant === 'number'
                  ? i.title || (i as any).values?.title || index + 1
                  : null;

              const audioPopupData = {
                type: AUDIO_POPUP,
                id: (i as any).id || `${item.id}-${index}`, // Use real subitem ID if available
                items: [],
                values: {
                  title: resolveHotspotMarker(
                    (i as any).iconType,
                    (i as any).iconValue,
                    legacyMarkerTitle
                  ),
                  iconType: (i as any).iconType ?? null,
                  iconValue: (i as any).iconValue ?? null,
                  left: resolvedLeft,
                  top: resolvedTop,
                  name: i.title || (i as any).values?.name || '',
                  body: i.body || (i as any).values?.body || '',
                  mediaUrl:
                    i.audios?.[0]?.mediaUrl ||
                    (i as any).values?.mediaUrl ||
                    '',
                  audioId: i.audios?.[0]?.id
                    ? Number(i.audios[0].id)
                    : (i as any).values?.audioId,
                  subtitlesUrl:
                    i.audios?.[0]?.subtitlesUrl ||
                    (i as any).values?.subtitlesUrl ||
                    '',
                  blocking: true,
                  replay: true,
                  isPopupCentered: true,
                  scroll: false,
                  pixelCoordinates: coordinates,
                  btnSize: BTN_SIZE
                }
              } as AudioPopupDefinition;

              return audioPopupData;
            } else if (i.videos && i.videos.length > 0) {
              const firstVideo = i.videos[0];
              return {
                type: VIDEO_POPUP,
                id: `${item.id}-${index}`,
                items: [
                  {
                    type: VIDEO,
                    id: `${item.id}-video`,
                    values: {
                      videoId: firstVideo?.id,
                      videoContainerStyles: {
                        width: '80%',
                        height: '70%',
                        borderRadius: '20px'
                      },
                      blocking: true,
                      isolated: false,
                      showPlayButton: true,
                      autoPlay: true,
                      mediaUrl:
                        isIOS || isSafari
                          ? ([
                              firstVideo?.mediaUrl && {
                                src: firstVideo.mediaUrl,
                                type: 'video/mp4'
                              }
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ].filter(Boolean) as any)
                          : ([
                              firstVideo?.hlsUrl && {
                                src: firstVideo.hlsUrl,
                                type: 'application/vnd.apple.mpegurl'
                              },
                              firstVideo?.mediaUrl && {
                                src: firstVideo.mediaUrl,
                                type: 'video/mp4'
                              }
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ].filter(Boolean) as any),
                      subtitlesUrl: firstVideo?.subtitlesUrl || '',
                      snapshotUrl: firstVideo?.snapshotUrl || ''
                    }
                  } as VideoDefinition
                ],
                values: {
                  title: item.variant == 'number' ? index + 1 : null,
                  left: resolvedLeft,
                  top: resolvedTop,
                  mediaUrl: i.videos?.[0]?.mediaUrl,
                  subtitlesUrl: i.videos?.[0]?.subtitlesUrl,
                  snapshotUrl: i.videos?.[0]?.snapshotUrl,
                  blocking: true,
                  replay: true
                }
              } as VideoPopupDefinition;
            } else {
              let titleForPopup = `${index + 1}`;

              if (initialParts[2]) {
                titleForPopup = initialParts[2];
              }

              const legacyMarkerTitle =
                item.variant == 'number' ? titleForPopup : null;

              return {
                type: AUDIO_POPUP,
                id: (i as any).id || `${item.id}-${index}`, // Use real subitem ID if available
                items: [],
                values: {
                  title: resolveHotspotMarker(
                    (i as any).iconType,
                    (i as any).iconValue,
                    legacyMarkerTitle
                  ),
                  iconType: (i as any).iconType ?? null,
                  iconValue: (i as any).iconValue ?? null,
                  left: resolvedLeft,
                  top: resolvedTop,
                  name: i.title,
                  body: i.body,
                  mediaUrl: i.audios?.[0]?.mediaUrl,
                  audioId: i.audios?.[0]?.id
                    ? Number(i.audios[0].id)
                    : undefined,
                  subtitlesUrl: i.audios?.[0]?.subtitlesUrl,
                  blocking: true,
                  replay: true,
                  isPopupCentered: true,
                  scroll: true,
                  pixelCoordinates:
                    i?.coordinates || (i as any)?.settings?.coordinates || '',
                  btnSize: BTN_SIZE
                }
              } as AudioPopupDefinition;
            }
          });
        })()
      } as ImageMap2Definition;
    }
    return null;
  });
};
